/**
 * Gallery page - Display all photos from all public characters
 */
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from '../../WebApp';

function Gallery() {
  const { language } = React.useContext(AuthContext);
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [genderFilter, setGenderFilter] = useState('all');
  const limit = 24;

  useEffect(() => {
    loadPhotos();
  }, [page, genderFilter]);

  const loadPhotos = async () => {
    if (page === 0) {
      setLoading(true);
    }
    setError(null);
    try {
      const params = { limit, offset: page * limit };
      if (genderFilter && genderFilter !== 'all') {
        params.gender = genderFilter;
      }
      const response = await axios.get('/web/characters/gallery/all', { params });
      
      const newPhotos = response.data.photos || [];
      
      if (page === 0) {
        setPhotos(newPhotos);
      } else {
        setPhotos((prev) => [...prev, ...newPhotos]);
      }
      
      setHasMore(newPhotos.length === limit && (page + 1) * limit < response.data.total);
    } catch (error) {
      console.error('Failed to load gallery photos:', error);
      setError(
        language === 'en'
          ? 'Failed to load gallery. Please try again later.'
          : 'Не удалось загрузить галерею. Попробуйте позже.'
      );
      setPhotos([]);
    } finally {
      setLoading(false);
    }
  };

  const loadMore = () => {
    if (!loading && hasMore) {
      setPage((prev) => prev + 1);
    }
  };

  const handleGenderChange = (g) => {
    setGenderFilter(g);
    setPage(0);
    setHasMore(true);
  };

  const translations = {
    en: {
      title: 'Gallery',
      subtitle: 'All photos from public characters',
      all: 'All',
      women: 'Women',
      men: 'Men',
      loading: 'Loading gallery...',
      loadMore: 'Load More',
      noPhotos: 'No photos available yet',
      by: 'by',
      viewCharacter: 'View Character',
    },
    ru: {
      title: 'Галерея',
      subtitle: 'Все фото публичных персонажей',
      all: 'Все',
      women: 'Женщины',
      men: 'Мужчины',
      loading: 'Загрузка галереи...',
      loadMore: 'Загрузить еще',
      noPhotos: 'Пока нет доступных фото',
      by: 'от',
      viewCharacter: 'Посмотреть персонажа',
    },
  };

  const t = translations[language] || translations.en;

  if (loading && photos.length === 0) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="text-xl font-semibold mb-2">{t.loading}</div>
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-burgundy-600 mx-auto"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      {/* Header */}
      <div className="mb-8 text-center">
        <h1 className="text-4xl sm:text-5xl font-extrabold mb-2 bg-gradient-to-r from-burgundy-400 to-pink-500 bg-clip-text text-transparent">
          {t.title}
        </h1>
        <p className="text-gray-400 text-sm sm:text-base">{t.subtitle}</p>
      </div>

      {/* Gender tabs */}
      <div className="flex justify-center gap-2 mb-6">
        {['all', 'female', 'male'].map((g) => (
          <button
            key={g}
            type="button"
            onClick={() => handleGenderChange(g)}
            className={`px-4 py-2 rounded-lg font-medium transition ${
              genderFilter === g
                ? 'bg-burgundy-600 text-white'
                : 'bg-gray-700 text-gray-400 hover:bg-gray-600 hover:text-white'
            }`}
          >
            {g === 'all' ? t.all : g === 'female' ? t.women : t.men}
          </button>
        ))}
      </div>

      {error && (
        <div className="mb-6 p-4 bg-red-900/30 border border-red-600/30 rounded-xl text-red-200 text-center">
          {error}
        </div>
      )}

      {photos.length === 0 && !loading ? (
        <div className="text-center py-12">
          <div className="text-gray-400 text-lg">{t.noPhotos}</div>
        </div>
      ) : (
        <>
          {/* Photo Grid */}
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-3 sm:gap-4 mb-8">
            {photos.map((photo) => (
              <Link
                key={photo.id}
                to={`/web/chat/${photo.character_id}`}
                className="group relative aspect-[9/16] rounded-xl overflow-hidden bg-dark-800 border border-white/10 hover:border-burgundy-500/50 transition-all duration-300 hover:scale-105"
              >
                <img
                  src={photo.image_url}
                  alt={photo.character_name}
                  className="w-full h-full object-cover"
                  loading="lazy"
                  onError={(e) => {
                    e.target.src = '/placeholder.jpg';
                  }}
                />
                {/* Overlay with character info */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/0 to-black/0 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <div className="absolute bottom-0 left-0 right-0 p-3">
                    <div className="flex items-center gap-2 mb-1">
                      {photo.character_avatar && (
                        <img
                          src={photo.character_avatar}
                          alt={photo.character_name}
                          className="w-6 h-6 rounded-full object-cover border border-white/20"
                        />
                      )}
                      <span className="text-white text-sm font-semibold truncate">
                        {photo.character_name}
                      </span>
                    </div>
                    <div className="text-xs text-gray-300">
                      {t.viewCharacter}
                    </div>
                  </div>
                </div>
              </Link>
            ))}
          </div>

          {/* Load More Button */}
          {hasMore && (
            <div className="text-center mb-8">
              <button
                onClick={loadMore}
                disabled={loading}
                className="px-6 py-3 rounded-xl bg-burgundy-600 hover:bg-burgundy-500 text-white font-semibold transition disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <span className="flex items-center gap-2">
                    <span className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white"></span>
                    {language === 'en' ? 'Loading...' : 'Загрузка...'}
                  </span>
                ) : (
                  t.loadMore
                )}
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default Gallery;

